import { useEffect, useRef } from 'react';

/**
 * Fires `onTimeout` after `timeoutMs` ms of no user interaction.
 * Resets on mousemove, keydown, pointerdown, scroll, or touchstart.
 * Default 30 min matches the Transaction BFF's idle TTL.
 */
export function useSessionTimeout(
  onTimeout: () => void,
  timeoutMs: number = 30 * 60 * 1000
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use a ref for the timeout handler to avoid resetting the effect when onTimeout changes
  const stableTimeoutRef = useRef(onTimeout);
  useEffect(() => {
    stableTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    const reset = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        stableTimeoutRef.current();
      }, timeoutMs);
    };

    const events = ['mousemove', 'keydown', 'pointerdown', 'scroll', 'touchstart'];
    events.forEach(e => {
      window.addEventListener(e, reset, { passive: true });
    });

    reset();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      events.forEach(e => {
        window.removeEventListener(e, reset);
      });
    };
  }, [timeoutMs]);
}
